import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Schema } from '@fhs/client';
import { MenuItemDetails } from '@fhs/menu/src/components/menu-item-details';
import { MenuItemSkeletonLoading } from '@fhs/menu/src/components/menu-item-skeleton';
import { ComboSelectionProvider } from '@fhs/menu/src/state/combo-selection';
import { MenuPickerProvider, useMenuPicker } from '@fhs/menu/src/state/menu-picker';
import { formatCentsToDollars } from '@fhs/menu/src/utils';
import {
  EditCartEntryResult,
  useEditCartEntry,
} from '@fhs/menu/src/utils/use-edit-from-cart-state';
import { useTransformForAddToCart } from '@fhs/menu/src/utils/use-transform-for-add-to-cart';
import {
  ActionSheet,
  Button,
  ErrorCard,
  MediaQueriesContext,
  createMqStyles,
  mobileMediaQueries,
  tokens,
} from '@fhs/ui';
import { ProductMenuObject } from '@fhs-legacy/frontend/src/state/product-wizard/types';
import { Nullable } from '@rbi-ctg/frontend';
import { ICartEntry } from '@rbi-ctg/menu';

import { ProductWizard } from '../../components';
import {
  useHandleOfferItemSelection,
  useOfferCart,
  useSelectedStep,
} from '../../state/offer-guide-cart-state';

import { BottomButtonContainer } from './bottom-button';
import { FadeView } from './content-renderer';

function EligibleItemRendererV2({
  shouldShowItemPrice,
  addItemToSimplyOfferCart,
  editCartEntryQuery,
  setOnBackAttempt,
}: {
  shouldShowItemPrice?: Nullable<boolean>;
  addItemToSimplyOfferCart: (cartEntry: ICartEntry) => void;
  editCartEntryQuery: EditCartEntryResult['query'];
  setOnBackAttempt: (callback: (() => boolean) | undefined) => void;
}) {
  const mqStyles = useMqStyles();
  const [isShowNutrition, setIsShowNutrition] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const menuPicker = useMenuPicker();
  const {
    shouldShowCustomizationScreen,
    setShouldShowCustomizationScreen,
    selectedPickerAspectOption,
  } = menuPicker;
  const shouldRenderPrice = Boolean(shouldShowItemPrice && menuPicker.priceCents);
  const allPickerAspectsAreUnavailable = !selectedPickerAspectOption;

  const transformForAddToCart = useTransformForAddToCart();

  const handleAddToCart = useCallback(async () => {
    try {
      setIsLoading(true);
      const itemId = menuPicker.selectedPickerAspectOption?.itemId;
      if (itemId) {
        const cartEntry = await transformForAddToCart({
          itemId,
          quantity: 1,
          isCombo: false,
          pickerSelections: menuPicker.pickerSelections,
          resolvedCustomizationSelections: menuPicker.resolvedCustomizationSelections,
          selectedPickerAspectOption: menuPicker.selectedPickerAspectOption,
        });

        cartEntry && addItemToSimplyOfferCart(cartEntry);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }, [
    transformForAddToCart,
    addItemToSimplyOfferCart,
    menuPicker.pickerSelections,
    menuPicker.resolvedCustomizationSelections,
    menuPicker.selectedPickerAspectOption,
  ]);

  useEffect(() => {
    if (shouldShowCustomizationScreen) {
      setOnBackAttempt(() => {
        setShouldShowCustomizationScreen(false);
        return true;
      });
    } else {
      setOnBackAttempt(undefined);
    }

    return () => setOnBackAttempt(undefined);
  }, [setOnBackAttempt, shouldShowCustomizationScreen, setShouldShowCustomizationScreen]);

  if (menuPicker.query.isLoading || editCartEntryQuery.isLoading) {
    return (
      <MediaQueriesContext.Provider value={mobileMediaQueries}>
        <View style={styles.loading}>
          <MenuItemSkeletonLoading hideFooter />
        </View>
      </MediaQueriesContext.Provider>
    );
  }

  if (menuPicker.query.isError || editCartEntryQuery.isError || !menuPicker.query.data) {
    return <ErrorCard description="Product not found" hideButton />;
  }

  return (
    <>
      <FadeView style={{ flex: 1 }}>
        <MediaQueriesContext.Provider value={mobileMediaQueries}>
          <MenuItemDetails
            renderScrollComponent={ActionSheet.ScrollView}
            isShowingNutrition={isShowNutrition}
            setIsShowingNutrition={setIsShowNutrition}
          />
        </MediaQueriesContext.Provider>
      </FadeView>
      {!shouldShowCustomizationScreen && (
        <BottomButtonContainer>
          <Button
            size="xl"
            onPress={handleAddToCart}
            loading={isLoading}
            disabled={allPickerAspectsAreUnavailable}
            style={[
              {
                justifyContent: shouldRenderPrice ? 'space-between' : 'center',
              },
              mqStyles.buttonContainer,
            ]}
          >
            <Button.Text>Add Item</Button.Text>
            {shouldRenderPrice && (
              <Button.Text>{formatCentsToDollars(menuPicker.priceCents) ?? ''}</Button.Text>
            )}
          </Button>
        </BottomButtonContainer>
      )}
    </>
  );
}

// todo no longer used after simply menu detail in offers, needs to be removed 
function EligibleItemRenderer({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  productId,
  addItemToSimplyOfferCart,
}: {
  productId: string;
  addItemToSimplyOfferCart: (cartEntry: ICartEntry) => void;
}) {
  // placeholder to not break build
  const product: ProductMenuObject | undefined = undefined;
  const loading = false;
  const { bottom } = useSafeAreaInsets();

  if (loading) {
    return (
      <MediaQueriesContext.Provider value={mobileMediaQueries}>
        <View style={styles.loading}>
          <MenuItemSkeletonLoading hideFooter />
        </View>
      </MediaQueriesContext.Provider>
    );
  }

  if (product) {
    return (
      <FadeView
        key="deprecated-product"
        style={{
          flex: 1,
          paddingBottom: bottom,
        }}
      >
        <ProductWizard
          product={product}
          isSimplyOffer
          addItemToSimplyOfferCart={addItemToSimplyOfferCart}
        />
      </FadeView>
    );
  }

  return <ErrorCard description="Product not found" hideButton />;
}
interface Props {
  selectedEligibleItem: Schema['MenuSectionItem']['type'];
}

export function SelectedEligibleItem({ selectedEligibleItem }: Props) {
  const {
    currentStepIndex,
    modifyItemSelectionInStep,
    isEdit,
    setOnBackAttempt,
    enableSimplifiedMenuDetailView,
    offerInfo,
  } = useOfferCart();

  const selectedStep = useSelectedStep();
  const handleOfferItemSelection = useHandleOfferItemSelection();

  const editCartEntry = useEditCartEntry(
    selectedStep?.selectedEntry,
    isEdit && enableSimplifiedMenuDetailView
  );
  if (enableSimplifiedMenuDetailView) {
    return (
      <MenuPickerProvider
        keepCustomizationInView
        slug={selectedEligibleItem.slug}
        isInitialStateLoading={editCartEntry.query.isLoading}
        selectedOfferId={offerInfo?.id}
        initialState={
          isEdit
            ? {
                selectedPickerAspectOptionId:
                  editCartEntry.query.data?.selectedPickerAspectOptionId ?? undefined,
                userCustomizationSelections: editCartEntry.query.data?.userCustomizationSelections,
                instructions: editCartEntry.query.data?.instructions,
              }
            : undefined
        }
      >
        <ComboSelectionProvider
          initialState={
            isEdit
              ? {
                  isComboEnabled: editCartEntry.query.data?.isComboEnabled,
                  comboSelectedPickers: undefined,
                }
              : undefined
          }
        >
          <EligibleItemRendererV2
            shouldShowItemPrice={selectedStep.shouldShowItemPrice}
            editCartEntryQuery={editCartEntry.query}
            addItemToSimplyOfferCart={cartEntry => {
              const cartEntryToAdd = {
                ...cartEntry,
                ...(isEdit && editCartEntry.cartEntry
                  ? {
                      // override generated cartId with the cart id of the item we're editing so it
                      //  replaces it in the cart instead of adding another entry.
                      cartId: editCartEntry.cartEntry.cartId,
                      lineId: editCartEntry.cartEntry.lineId,
                    }
                  : {}),
              };
              handleOfferItemSelection(currentStepIndex, cartEntryToAdd);
            }}
            setOnBackAttempt={setOnBackAttempt}
          />
        </ComboSelectionProvider>
      </MenuPickerProvider>
    );
  }

  return (
    <EligibleItemRenderer
      productId={selectedEligibleItem._legacySlug}
      addItemToSimplyOfferCart={cartEntry => {
        modifyItemSelectionInStep(currentStepIndex, cartEntry);
      }}
    />
  );
}

const styles = StyleSheet.create({
  loading: {
    flex: 1,
    backgroundColor: tokens.colors.$white,
  },
});

const useMqStyles = createMqStyles({
  buttonContainer: {
    $base: {
      flexDirection: 'row',
      paddingHorizontal: 28,
    },
    $gteDesktop: {
      paddingHorizontal: 38,
    },
  },
});
